import { Loader } from '@subframe/core/dist/cjs';
import { UpgradeHistoryEvent, UpgradePlan, UpgradeStage } from 'api/models';
import { AtomicTooltip } from 'components/design-system';
import { format, formatDistanceToNow } from 'date-fns';
import {
  AccordionWithUnderline,
  Avatar,
  DetailsList,
  Feed,
  IconWithBackground,
  Progress,
} from 'subframe/index';
import {
  getUpgradeModeHumanReadable,
  UpgradeAny,
  UpgradeViewMode,
} from '../helpers/UpgradesHelpers';
import styles from './UpgradeSidebar.module.scss';
import { getUpgradeAnyStatusBadge } from '../helpers/UpgradesViewHelpers';
import {
  useGetUpgradeTemplate,
  useListUpgradeAssessmentHistory,
  useListUpgradePlanHistory,
  useListUpgradeTemplateHistory,
} from 'api/frontend';
import useUserAccountState from 'hooks/useUserAccountState';
import { UPGRADE_TEMPLATES } from 'src/data/upgrades';
import { getHumanReadableDateAndTime } from 'utils/formatTime';

export function getHistoryText(
  event: UpgradeHistoryEvent,
  mode: UpgradeViewMode,
): string {
  switch (event.type) {
    case 'step_marked_for_upgrade':
      return `marked the step '${event.subject.step?.name}' 'For Upgrade'`;
    case 'step_not_marked_for_upgrade':
      return `marked the step '${event.subject.step?.name}' as 'Don't Upgrade'`;
    case 'plan_created':
      return `created the ${getUpgradeModeHumanReadable(mode)}`;
    case 'plan_completed':
      return `completed the ${getUpgradeModeHumanReadable(mode)}`;
    case 'step_added':
      return `added a new step '${event.subject.step?.name}`;
    case 'step_updated':
      return '';
    case 'step_deleted':
      return `deleted the step '${event.subject.step?.name}'`;
    case 'step_skipped':
      return `skipped the step '${event.subject.step?.name}'`;
    case 'step_unskipped':
      return `reverted the step '${event.subject.step?.name}'`;
    case 'step_completed':
      return `marked the step '${event.subject.step?.name}' as completed`;
    case 'step_uncompleted':
      return `reverted the step '${event.subject.step?.name}'`;
    case 'step_commented':
      return `commented on '${event.subject.step?.name}'`;
    case 'stage_completed':
    case 'stage_uncompleted':
      return '';
    default:
      return '';
  }
}

export function getHistoryAvatar(event: UpgradeHistoryEvent): React.ReactNode {
  switch (event.type) {
    case 'step_completed':
      return (
        <IconWithBackground
          variant="success"
          size="small"
          icon="FeatherCheck"
        />
      );
    case 'step_skipped':
      return (
        <IconWithBackground
          variant="neutral"
          size="small"
          icon="FeatherMinus"
        />
      );
    case 'step_uncompleted':
    case 'step_unskipped':
      return (
        <IconWithBackground variant="error" size="small" icon="FeatherUndo" />
      );
    default:
      return event.actor.user === undefined ? (
        <Avatar size="small" image="/icons/Logo_ndnxzk.png" />
      ) : (
        <Avatar
          size="small"
          image={event.actor.user.picture}
          className="ph-no-capture"
        />
      );
  }
}

interface UpgradeSidebarProps {
  upgrade: UpgradeAny;
  mode: UpgradeViewMode;
  stages: UpgradeStage[];
  showExampleData: boolean;
}

export function UpgradeSidebar(props: UpgradeSidebarProps) {
  const { account } = useUserAccountState();

  let upgradeHistoryResponse: {
    data: { data: UpgradeHistoryEvent[] } | undefined;
  } = { data: undefined };
  switch (props.mode) {
    case UpgradeViewMode.Plan:
      upgradeHistoryResponse = useListUpgradePlanHistory(
        props.upgrade.id,
        {},
        {
          request: {
            headers: { Authorization: `Bearer ${account.token}` },
          },
          swr: {
            enabled: !props.showExampleData,
          },
        },
      );
      break;
    case UpgradeViewMode.Template:
      upgradeHistoryResponse = useListUpgradeTemplateHistory(
        props.upgrade.id,
        {},
        {
          request: {
            headers: { Authorization: `Bearer ${account.token}` },
          },
          swr: {
            enabled: !props.showExampleData,
          },
        },
      );
      break;
    case UpgradeViewMode.Assessment:
      upgradeHistoryResponse = useListUpgradeAssessmentHistory(
        props.upgrade.id,
        {},
        {
          request: {
            headers: { Authorization: `Bearer ${account.token}` },
          },
          swr: {
            enabled: !props.showExampleData,
          },
        },
      );
      break;
  }

  const totalSteps = props.stages
    .map(
      (stage) =>
        stage.steps.filter((step) => step.requirement_level !== 'optional')
          .length,
    )
    .reduce((a, b) => a + b, 0);
  const completedSteps = props.stages
    .map(
      (stage) =>
        stage.steps.filter(
          (step) =>
            step.requirement_level !== 'optional' &&
            (step.status === 'completed' || step.status === 'skipped'),
        ).length,
    )
    .reduce((a, b) => a + b, 0);

  let additionalListItems: JSX.Element[] = [];
  if (
    props.mode === UpgradeViewMode.Plan ||
    props.mode === UpgradeViewMode.Template
  ) {
    const upgradePlan = props.upgrade as UpgradePlan;
    if (
      upgradePlan.status === 'completed' &&
      upgradePlan.completed_by !== undefined
    ) {
      additionalListItems.push(
        <DetailsList.DetailsListRow
          label={`Marked ${
            props.mode === UpgradeViewMode.Template
              ? 'Environment Upgraded'
              : 'Completed'
          } by`}
          icon="FeatherUser"
          className="ph-no-capture"
          value={upgradePlan.completed_by.name}
        />,
      );
    }
  }

  if (props.mode === UpgradeViewMode.Plan) {
    const plan = props.upgrade as UpgradePlan;
    if (plan.upgrade_template_id) {
      if (props.showExampleData) {
        const upgradeTemplate = UPGRADE_TEMPLATES.find(
          (t) => t.id === plan.upgrade_template_id,
        );
        if (upgradeTemplate) {
          additionalListItems.push(
            <DetailsList.DetailsListRow
              label="Upgrade Template"
              icon="FeatherList"
              value={upgradeTemplate.name}
            />,
          );
        }
      } else {
        const { data: upgradeTemplateData } = useGetUpgradeTemplate(
          plan.upgrade_template_id,
          {
            request: { headers: { Authorization: `Bearer ${account.token}` } },
            swr: {
              enabled: !props.showExampleData,
            },
          },
        );
        if (upgradeTemplateData) {
          additionalListItems.push(
            <DetailsList.DetailsListRow
              label="Upgrade Template"
              icon="FeatherList"
              value={upgradeTemplateData.name}
            />,
          );
        }
      }
    }
  }

  if (props.mode === UpgradeViewMode.Plan) {
    additionalListItems.push(
      <DetailsList.DetailsListRow
        label="Progress"
        icon="FeatherCircleEllipsis"
        value={
          <AtomicTooltip
            tooltipContent={`${completedSteps} of ${totalSteps} steps completed`}
          >
            <Progress
              value={totalSteps ? (completedSteps / totalSteps) * 100 : 0}
              className="w-[250px]"
            />
          </AtomicTooltip>
        }
      />,
    );
  }

  if (props.mode === UpgradeViewMode.Assessment) {
    additionalListItems.push(
      <DetailsList.DetailsListRow
        label="Requested on"
        icon="FeatherList"
        value={getHumanReadableDateAndTime(props.upgrade.created)}
      />,
    );
  }

  return (
    <div className={styles['stack-d78aa573']}>
      <DetailsList>
        <DetailsList.DetailsListBadgeRow
          label="Status"
          badges={getUpgradeAnyStatusBadge(props.upgrade.status, props.mode)}
        />
        {props.upgrade.requested_by !== undefined && (
          <DetailsList.DetailsListRow
            label="Requested By"
            icon="FeatherUser"
            className="ph-no-capture"
            value={props.upgrade.requested_by.name}
          />
        )}
        {additionalListItems}
      </DetailsList>
      <AccordionWithUnderline headerText="Activity Feed" defaultOpen={true}>
        <div className={styles['accordion-contents']}>
          {!props.showExampleData &&
            upgradeHistoryResponse.data === undefined && <Loader />}
          {!props.showExampleData && upgradeHistoryResponse.data && (
            <Feed className="h-full w-full grow shrink-0 basis-0">
              {upgradeHistoryResponse.data.data
                .sort((a, b) => b.time - a.time)
                .map((item, idx) => (
                  <Feed.Item
                    key={idx}
                    leftSlot={getHistoryAvatar(item)}
                    comment={
                      item.data.comment !== undefined &&
                      item.data.comment !== '' && (
                        <Feed.Comment>{item.data.comment}</Feed.Comment>
                      )
                    }
                    timestamp={formatDistanceToNow(new Date(item.time * 1000), {
                      addSuffix: true,
                    })}
                    isLast={
                      idx ===
                      (upgradeHistoryResponse.data?.data.length || 0) - 1
                    }
                  >
                    <span className="text-body-bold font-body-bold text-default-font ph-no-capture">
                      {item.actor.user?.name || 'Chkk'}
                    </span>
                    <span className="text-body font-body text-subtext-color">
                      {getHistoryText(item, props.mode)}
                    </span>
                  </Feed.Item>
                ))}
            </Feed>
          )}
          {props.showExampleData && (
            <Feed>
              {props.mode === UpgradeViewMode.Plan && (
                <Feed.Item
                  leftSlot={
                    <IconWithBackground
                      variant="success"
                      size="small"
                      icon="FeatherCheck"
                    />
                  }
                  timestamp={
                    props.upgrade.updated
                      ? formatDistanceToNow(new Date(props.upgrade.updated), {
                          addSuffix: true,
                        })
                      : ''
                  }
                  comment={null}
                >
                  <span className="text-body-bold font-body-bold text-default-font ph-no-capture">
                    {(props.upgrade as UpgradePlan).completed_by?.name ||
                      'Jane Doe'}
                  </span>
                  <span className="text-body font-body text-subtext-color">
                    {'completed this Upgrade Plan'}
                  </span>
                </Feed.Item>
              )}
              <Feed.Item
                leftSlot={
                  <IconWithBackground
                    variant="neutral"
                    size="small"
                    icon="FeatherUser"
                  />
                }
                timestamp={formatDistanceToNow(
                  new Date(props.upgrade.created),
                  {
                    addSuffix: true,
                  },
                ).toString()}
                comment={null}
                isLast={true}
              >
                <span className="text-body-bold font-body-bold text-default-font ph-no-capture">
                  {props.upgrade.requested_by?.name || 'John Doe'}
                </span>
                <span className="text-body font-body text-subtext-color">
                  {`requested this ${getUpgradeModeHumanReadable(props.mode)}`}
                </span>
              </Feed.Item>
            </Feed>
          )}
        </div>
      </AccordionWithUnderline>
    </div>
  );
}
